import { Mic, MicOff, Sparkles } from "lucide-react";

interface VoiceAssistantPanelProps {
  available: boolean;
  supported: boolean;
  listening: boolean;
  speaking: boolean;
  connecting?: boolean;
  status?: string;
  error?: string;
  onToggle: () => void;
  onStop: () => void;
}

export default function VoiceAssistantPanel({
  available,
  supported,
  listening,
  speaking,
  connecting,
  status,
  error,
  onToggle,
  onStop,
}: VoiceAssistantPanelProps) {
  if (!available) {
    return null;
  }

  const isActive = listening || speaking || Boolean(connecting);
  const actionLabel = isActive ? "Stop voice" : "Start voice";
  const isGreeting = Boolean(status && status.toLowerCase().includes("assistant greeting"));
  const statusLabel = error
    ? error
    : connecting
      ? "Connecting to voice..."
      : status || (listening ? "Listening..." : speaking ? "Speaking..." : "Voice ready");
  const headline = error
    ? "Voice unavailable"
    : connecting
      ? "Connecting"
      : isGreeting
        ? "Assistant greeting..."
      : listening
        ? "Listening"
        : speaking
          ? "Speaking"
          : "Voice ready";
  const helperText = error
    ? error
    : connecting
      ? "Warming up the voice channel and preparing audio."
      : isGreeting
        ? "The assistant is speaking first. Mic is temporarily paused."
      : listening
        ? "Your microphone is live. Speak naturally."
      : speaking
          ? "The assistant is replying with audio."
          : "Tap the orb to start a voice session.";
  const accent = listening
    ? "rgba(37, 99, 235, 0.20)"
    : speaking
      ? "rgba(14, 165, 233, 0.20)"
      : "rgba(99, 102, 241, 0.16)";

  const handleClick = () => {
    if (!supported) {
      return;
    }

    if (isActive) {
      onStop();
      return;
    }

    onToggle();
  };

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        maxWidth: "420px",
        margin: "0 auto 10px",
        padding: "18px 18px 16px",
        borderRadius: "28px",
        border: `1px solid ${supported ? "rgba(148, 163, 184, 0.22)" : "rgba(203, 213, 225, 0.8)"}`,
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.94) 100%)",
        boxShadow: "0 18px 48px rgba(15, 23, 42, 0.10)",
        backdropFilter: "blur(14px)",
        overflow: "hidden",
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.7), transparent 36%), radial-gradient(circle at top left, rgba(14,165,233,0.10), transparent 32%), radial-gradient(circle at right center, rgba(99,102,241,0.10), transparent 28%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "18px",
          minWidth: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            gap: "12px",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "6px 10px",
                borderRadius: "999px",
                background: supported ? "rgba(15, 23, 42, 0.04)" : "rgba(148, 163, 184, 0.12)",
                color: supported ? "#0f172a" : "#64748b",
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                width: "fit-content",
              }}
            >
              <Sparkles size={10} />
              Voice assistant
            </div>
          <div
            style={{
              marginTop: "8px",
              fontSize: "22px",
              fontWeight: 800,
              letterSpacing: "-0.03em",
              color: "#0f172a",
              lineHeight: 1,
            }}
          >
            {headline}
          </div>
          <div
            style={{
              marginTop: "8px",
              maxWidth: "260px",
              fontSize: "12px",
              lineHeight: 1.5,
              color: error ? "#b91c1c" : "#475569",
            }}
          >
            {helperText}
          </div>
        </div>

          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              padding: "6px 10px",
              borderRadius: "999px",
              background: supported ? "rgba(16, 185, 129, 0.12)" : "rgba(148, 163, 184, 0.16)",
              color: supported ? "#047857" : "#64748b",
              fontSize: "10px",
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              flexShrink: 0,
              whiteSpace: "nowrap",
            }}
          >
            {supported ? (isActive ? "Live" : "Ready") : "Unavailable"}
          </span>
        </div>

        <div
          style={{
            position: "relative",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            padding: "14px 0 8px",
          }}
        >
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              width: "290px",
              height: "290px",
              borderRadius: "999px",
              background:
                "radial-gradient(circle at center, rgba(14,165,233,0.12), rgba(14,165,233,0) 64%)",
              filter: "blur(16px)",
            }}
          />
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              width: "240px",
              height: "240px",
              borderRadius: "999px",
              background:
                "radial-gradient(circle at center, rgba(99,102,241,0.12), rgba(99,102,241,0) 62%)",
              filter: "blur(18px)",
            }}
          />

          <div
            style={{
              position: "relative",
              width: "248px",
              height: "248px",
              borderRadius: "999px",
              background: supported
                ? "conic-gradient(from 180deg, rgba(13,148,136,0.10), rgba(37,99,235,0.78), rgba(255,255,255,0.94), rgba(45,212,191,0.42), rgba(37,99,235,0.88), rgba(255,255,255,0.96), rgba(13,148,136,0.10))"
                : "conic-gradient(from 180deg, rgba(148,163,184,0.18), rgba(226,232,240,0.92), rgba(148,163,184,0.18))",
              boxShadow: supported
                ? `inset 0 0 0 1px rgba(255,255,255,0.7), 0 18px 36px ${accent}`
                : "inset 0 0 0 1px rgba(255,255,255,0.6)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: "16px",
                borderRadius: "999px",
                background:
                  "radial-gradient(circle at 50% 38%, rgba(255,255,255,0.98), rgba(255,255,255,0.72) 56%, rgba(255,255,255,0.28) 78%, rgba(255,255,255,0) 100%)",
                boxShadow: "inset 0 1px 12px rgba(15,23,42,0.06)",
              }}
            >
            </div>

            <div
              style={{
                position: "absolute",
                inset: "34px",
                borderRadius: "999px",
                background:
                  "conic-gradient(from 90deg, rgba(14,165,233,0.6), rgba(224,242,254,0.94), rgba(37,99,235,0.72), rgba(191,219,254,0.92), rgba(14,165,233,0.6))",
                opacity: 0.94,
                filter: "blur(1px)",
                animation: supported && isActive ? "voiceSpin 12s linear infinite" : "none",
              }}
            />

            <div
              style={{
                position: "absolute",
                inset: "64px",
                borderRadius: "999px",
                background: "#fff",
                boxShadow: "inset 0 1px 10px rgba(15,23,42,0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(6, 1fr)",
                  gap: "2px",
                  width: "78px",
                  height: "78px",
                  alignItems: "end",
                  transform: "translateY(2px)",
                }}
              >
                {Array.from({ length: 36 }).map((_, index) => {
                  const barHeight = listening || speaking ? [34, 56, 42, 64, 48, 58, 38, 70][index % 8] : [22, 32, 24, 34, 26, 30, 20, 28][index % 8];
                  const isBright = listening ? index % 3 !== 0 : speaking ? index % 4 !== 0 : index % 2 === 0;
                  return (
                    <div
                      key={index}
                      style={{
                        height: `${barHeight}%`,
                        borderRadius: "999px",
                        background: isBright
                          ? "linear-gradient(to top, #2563eb, #67e8f9)"
                          : "linear-gradient(to top, #0f766e, #a5f3fc)",
                        opacity: listening || speaking ? 1 : 0.7,
                      }}
                    />
                  );
                })}
              </div>
            </div>

            {supported && (listening || speaking || connecting) && (
              <span
                aria-hidden="true"
                style={{
                  position: "absolute",
                  inset: "-8px",
                  borderRadius: "999px",
                  border: "1px solid rgba(99, 102, 241, 0.22)",
                  animation: "voicePulse 1.8s ease-in-out infinite",
                }}
              />
            )}
          </div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "10px",
          marginTop: "18px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            minWidth: 0,
          }}
        >
          <div
            style={{
              fontSize: "12px",
              fontWeight: 700,
              color: error ? "#b91c1c" : "#334155",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "260px",
            }}
            title={statusLabel}
          >
            {statusLabel}
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <span
              style={{
                height: "4px",
                width: "18px",
                borderRadius: "999px",
                background: listening || speaking ? "#2563eb" : "#cbd5e1",
              }}
            />
            <span
              style={{
                height: "4px",
                width: "10px",
                borderRadius: "999px",
                background: speaking ? "#0ea5e9" : "#cbd5e1",
              }}
            />
            <span
              style={{
                height: "4px",
                width: "14px",
                borderRadius: "999px",
                background: listening ? "#6366f1" : "#cbd5e1",
              }}
            />
            <span
              style={{
                height: "4px",
                width: "8px",
                borderRadius: "999px",
                background: connecting ? "#8b5cf6" : "#cbd5e1",
              }}
            />
          </div>
        </div>

        <button
          type="button"
          onClick={handleClick}
          disabled={!supported}
          style={{
            flexShrink: 0,
            border: "1px solid rgba(99, 102, 241, 0.16)",
            borderRadius: "999px",
            padding: "10px 14px",
            fontSize: "12px",
            fontWeight: 800,
            letterSpacing: "0.01em",
            whiteSpace: "nowrap",
            color: supported ? "#0f172a" : "#94a3b8",
            background: isActive
              ? "linear-gradient(135deg, rgba(37, 99, 235, 0.14), rgba(14, 165, 233, 0.10))"
              : "rgba(255,255,255,0.90)",
            cursor: supported ? "pointer" : "not-allowed",
            boxShadow: "0 6px 16px rgba(15, 23, 42, 0.05)",
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
            {isActive ? <MicOff size={14} /> : <Mic size={14} />}
            {actionLabel}
          </span>
        </button>
      </div>

      <style>{`
        @keyframes voicePulse {
          0% { transform: scale(0.96); opacity: 0.9; }
          70% { transform: scale(1.08); opacity: 0.15; }
          100% { transform: scale(0.96); opacity: 0.9; }
        }
        @keyframes voiceSpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
